import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid } from '@mui/material';
import {
  ControlledField,
  MessageGroup,
  MessageGroupMember,
  RecipientType,
  SelectInputField,
  SelectOption,
  TextareaInputField,
} from 'remote/global';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PaperLayout from 'components/PaperLayout';
import {
  useOperatorsUserMessagingGroupsQuery,
  useOperatorsUserMessagingUsersQuery,
  usePostOperatorsUserMessagingMessagesMutation,
} from 'api/queries/operators';
import { toast } from 'react-hot-toast';
import { NewMessageSchemaType, UserMessageDtoSchemaType, newMessageSchema } from './types';

const createOptions = (t: TFunction): SelectOption[] =>
  Object.keys(RecipientType).map((k) => ({ label: t(`RecipientType.${k}`), value: k }));

function CreateNewMessageForm() {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { data: messageGroupUsers } = useOperatorsUserMessagingUsersQuery({
    pageNo: '0',
  });
  const { data: messageGroups } = useOperatorsUserMessagingGroupsQuery();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<NewMessageSchemaType>({
    defaultValues: {
      recipientType: RecipientType.ALL,
      recipient: 0,
      content: '',
      subject: '',
    },
    resolver: zodResolver(newMessageSchema),
  });
  const postOperatorsUserMessagingMessagesMutation =
    usePostOperatorsUserMessagingMessagesMutation();

  const options = createOptions(t);
  const recipientType = watch('recipientType');
  const getRecipientOptions = (): SelectOption[] => {
    switch (recipientType) {
      case RecipientType.GROUP: {
        const opts = (messageGroups as MessageGroup[]).map((g) => ({
          label: g.name,
          value: g.id,
        }));
        return opts;
      }
      case RecipientType.USER: {
        const opts = (messageGroupUsers?.elements as MessageGroupMember[]).map((u) => ({
          label: u.contactInfo?.fullName ?? '',
          value: u.userId,
        }));
        return opts;
      }
      default:
        return [];
    }
  };
  const recipientOptions = getRecipientOptions();

  const onValid = async (data: NewMessageSchemaType) => {
    const payload: UserMessageDtoSchemaType = {
      recipientType: data.recipientType,
      subject: data.subject,
      content: data.content,
    };

    if (data.recipient !== 0) {
      switch (data.recipientType) {
        case RecipientType.GROUP:
          payload.groupId = String(data.recipient);
          break;
        case RecipientType.USER:
          payload.userId = data.recipient;
          break;
        default:
      }
    }

    await postOperatorsUserMessagingMessagesMutation.mutateAsync(payload);
    toast.success(t('Toast.success'));
    // Resets the form
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <PaperLayout
        title={t('MessagesView.composeMessage.title')}
        subtitle={t('MessagesView.composeMessage.subtitle')}
      >
        <Grid container>
          <Grid item xs={12}>
            <ControlledField
              control={control}
              name='recipientType'
              render={(renderProps) => {
                const { field, ...rest } = renderProps;

                return (
                  <SelectInputField
                    {...rest}
                    field={{
                      ...field,
                      onChange: (evt: any, elm: any) => {
                        field.onChange(evt);
                        const selection = elm?.props?.value;
                        // Reset the recipient field if this value changes
                        if (recipientType !== selection) resetField('recipient');
                      },
                    }}
                    options={options}
                  />
                );
              }}
            />
            {recipientOptions.length > 0 && (
              <ControlledField
                control={control}
                name='recipient'
                render={(renderProps) => (
                  <SelectInputField {...renderProps} options={recipientOptions} />
                )}
              />
            )}
            <ControlledField control={control} name='subject' />
            <ControlledField
              control={control}
              name='content'
              render={(renderProps) => (
                <TextareaInputField {...renderProps} fieldProps={{ minRows: 4 }} />
              )}
            />
          </Grid>
          <Button
            sx={{ mt: '1rem' }}
            fullWidth
            type='submit'
            variant='contained'
            disabled={isSubmitting || isValidating || isLoading}
          >
            {t('submit')}
          </Button>
        </Grid>
      </PaperLayout>
    </form>
  );
}

export default CreateNewMessageForm;
